import {
  Button,
  Heading,
  Input,
  LinkButton,
  Select,
  Spinner,
  Tag,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { Lock } from "iconoir-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { SwitchCard } from "shared/components/molecules/SwitchCard";
import {
  OrganizationByIdDocument,
  OrganizationsByUserDocument,
  RenewalEngineSetupPreviewDocument,
  TagManyDocument,
  useRenewalEngineEnableMutation,
  useRenewalEngineSetupPreviewQuery,
} from "gql/generated";

type TagOption = { value: string; label: string };
type Role = "ACTIVE" | "RENEWING" | "EXPIRED";
type TagVisual = "success" | "warning" | "destructive" | "primary";

const ROLES: Role[] = ["ACTIVE", "RENEWING", "EXPIRED"];
const DEFAULT_GRACE_DAYS = 90;

const ROLE_VISUAL: Record<Role, TagVisual> = {
  ACTIVE: "success",
  RENEWING: "warning",
  EXPIRED: "primary",
};

type RenewalEngineSetupCardProps = {
  organizationId: string;
  /** Renewal automation is saved as on, with a date-driven term. */
  savedEnabled: boolean;
  statusTagOptions: TagOption[];
  /** Unset = default (90 days); 0 = grace period off. */
  graceDays: number | undefined;
  onGraceDaysChange: (days: number | undefined) => void;
  neverExpire: boolean;
  onNeverExpireChange: (neverExpire: boolean) => void;
  overdueTagId?: string | null;
  cancelledTagId?: string | null;
  membershipTranslationValues: Record<string, string>;
};

/**
 * Replaces the Active/Renewing/Expired dropdowns for orgs on the
 * `renewal_engine_owned_tags` flag: finds or creates the three tags in one
 * step and shows them locked once renewal automation owns them.
 */
export const RenewalEngineSetupCard = ({
  organizationId,
  savedEnabled,
  statusTagOptions,
  graceDays,
  onGraceDaysChange,
  neverExpire,
  onNeverExpireChange,
  overdueTagId,
  cancelledTagId,
  membershipTranslationValues,
}: RenewalEngineSetupCardProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS.RENEWAL.ENGINE_SETUP",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const [changing, setChanging] = useState(false);
  const [overrides, setOverrides] = useState<Partial<Record<Role, string>>>({});

  const { data, loading } = useRenewalEngineSetupPreviewQuery({
    variables: { organizationId },
    skip: !savedEnabled,
    fetchPolicy: "cache-and-network",
  });
  const preview = data?.renewalEngineSetupPreview;

  const [enable, { loading: enabling }] = useRenewalEngineEnableMutation({
    refetchQueries: [
      OrganizationByIdDocument,
      OrganizationsByUserDocument,
      RenewalEngineSetupPreviewDocument,
      TagManyDocument,
    ],
    awaitRefetchQueries: true,
  });

  const collection = useMemo(
    () => Select.createCollection({ items: statusTagOptions }),
    [statusTagOptions],
  );

  if (!savedEnabled) {
    return (
      <Text size="sm" color="text.mid">
        {t("SAVE_FIRST", membershipTranslationValues)}
      </Text>
    );
  }
  if (loading && !preview) return <Spinner w="16px" h="16px" />;
  if (!preview) return null;

  const detected = new Map(preview.detected.map(d => [d.role as Role, d]));
  const nameFor = (role: Role) => {
    const override = overrides[role];
    if (override) {
      return statusTagOptions.find(o => o.value === override)?.label ?? "";
    }
    return detected.get(role)?.tagName ?? "";
  };
  const labelForId = (id: string | null | undefined, fallback: string) =>
    (id && statusTagOptions.find(o => o.value === id)?.label) || fallback;

  const cardProps = {
    flexDir: "column",
    gap: "200",
    p: "300",
    borderWidth: "1px",
    borderColor: "border.light",
    borderRadius: "200",
  } as const;

  const effectiveGraceDays = graceDays ?? DEFAULT_GRACE_DAYS;
  const graceOn = effectiveGraceDays > 0;

  if (preview.engineOwned) {
    const ownedTags: { key: string; name: string; visual: TagVisual }[] = [
      { key: "ACTIVE", name: nameFor("ACTIVE"), visual: ROLE_VISUAL.ACTIVE },
      { key: "RENEWING", name: nameFor("RENEWING"), visual: ROLE_VISUAL.RENEWING },
    ];
    if (graceOn || neverExpire || overdueTagId) {
      ownedTags.push({
        key: "OVERDUE",
        name: labelForId(overdueTagId, t("ROLE_OVERDUE")),
        visual: "destructive",
      });
    }
    ownedTags.push({
      key: "EXPIRED",
      name: nameFor("EXPIRED"),
      visual: ROLE_VISUAL.EXPIRED,
    });
    if (cancelledTagId) {
      ownedTags.push({
        key: "CANCELLED",
        name: labelForId(cancelledTagId, t("ROLE_CANCELLED")),
        visual: "primary",
      });
    }

    return (
      <Flex {...cardProps}>
        <Heading size="h6">{t("OWNED_TITLE")}</Heading>
        <Flex gap="100" flexWrap="wrap">
          {ownedTags.map(tag => (
            <Tag
              key={tag.key}
              size="sm"
              mode="light"
              visual={tag.visual}
              Icon={Lock}
              iconProps={{ size: "sm" }}
              w="fit-content"
              style={
                tag.key === "EXPIRED"
                  ? { backgroundColor: "transparent", border: "1px solid black" }
                  : undefined
              }
            >
              {tag.name}
            </Tag>
          ))}
        </Flex>
        <Text size="sm" color="text.mid">
          {t("OWNED_DESCRIPTION", membershipTranslationValues)}
        </Text>
        <SwitchCard
          title={t("NEVER_EXPIRE_TOGGLE", membershipTranslationValues)}
          subtitle={t("NEVER_EXPIRE_HINT", membershipTranslationValues)}
          checked={neverExpire}
          onCheckedChange={onNeverExpireChange}
        />
        {!neverExpire && (
          <>
            <SwitchCard
              title={t("GRACE_TOGGLE")}
              subtitle={
                graceOn
                  ? t("GRACE_ON_HINT", {
                      ...membershipTranslationValues,
                      days: effectiveGraceDays,
                    })
                  : t("GRACE_OFF_HINT", membershipTranslationValues)
              }
              checked={graceOn}
              onCheckedChange={checked =>
                onGraceDaysChange(checked ? DEFAULT_GRACE_DAYS : 0)
              }
            />
            {graceOn && (
              <Input
                label={t("GRACE_LABEL")}
                type="number"
                min={1}
                placeholder={String(DEFAULT_GRACE_DAYS)}
                value={graceDays ?? ""}
                onChange={e => {
                  const value = e.target.value;
                  onGraceDaysChange(
                    value === "" ? undefined : Math.max(1, Number(value) || 1),
                  );
                }}
              />
            )}
          </>
        )}
      </Flex>
    );
  }

  const willCreate = ROLES.filter(
    role => !overrides[role] && detected.get(role)?.source === "CREATE",
  );

  const handleEnable = async () => {
    try {
      const { data: result } = await enable({
        variables: {
          organizationId,
          activeTagId: overrides.ACTIVE ?? null,
          renewingTagId: overrides.RENEWING ?? null,
          expiredTagId: overrides.EXPIRED ?? null,
        },
      });
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("ENABLE_SUCCESS", {
          count: result?.renewalEngineEnable.changed ?? 0,
        }),
      });
    } catch (error) {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description:
          error instanceof Error && error.message
            ? error.message
            : t("ENABLE_ERROR"),
      });
    }
  };

  return (
    <Flex {...cardProps}>
      <Heading size="h6">{t("TITLE")}</Heading>
      <Text size="sm" color="text.dark">
        {t("USING", {
          active: nameFor("ACTIVE"),
          renewing: nameFor("RENEWING"),
          expired: nameFor("EXPIRED"),
        })}
      </Text>
      {willCreate.length > 0 && (
        <Text size="sm" color="text.mid">
          {t("WILL_CREATE", {
            names: willCreate.map(nameFor).join(", "),
          })}
        </Text>
      )}
      {preview.membersChanging > 0 && (
        <Text size="sm" color="text.mid">
          {t("MEMBERS_CHANGING", {
            ...membershipTranslationValues,
            count: preview.membersChanging,
          })}
        </Text>
      )}
      <Text size="sm" color="text.light">
        {t("LOCK_NOTE")}
      </Text>

      {changing ? (
        <Flex flexDir="column" gap="200">
          {ROLES.map(role => (
            <Select.Root
              key={role}
              collection={collection}
              value={
                overrides[role]
                  ? [overrides[role]!]
                  : detected.get(role)?.tagId
                    ? [detected.get(role)!.tagId!]
                    : []
              }
              onValueChange={d =>
                setOverrides(prev => ({ ...prev, [role]: d.value[0] }))
              }
              label={t(`ROLE_${role}`)}
              placeholder={t("CREATE_NEW", { name: nameFor(role) })}
            >
              {statusTagOptions.map(option => (
                <Select.Item key={option.value} item={option} />
              ))}
            </Select.Root>
          ))}
        </Flex>
      ) : (
        <LinkButton
          type="button"
          alignSelf="flex-start"
          onClick={() => setChanging(true)}
        >
          {t("CHANGE")}
        </LinkButton>
      )}

      <Button
        visual="accent.blue"
        type="button"
        alignSelf="flex-start"
        onClick={handleEnable}
        isLoading={enabling}
        disabled={enabling}
      >
        {t("ENABLE")}
      </Button>
    </Flex>
  );
};
